import React, { useState } from 'react';
import Box from '@mui/system/Box';
import { useStacklineTheme } from '@stackline/ui';
import { GenericNavigationTabs } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import AlertTemplatesTab from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/common/AlertTemplatesTab';
import { AlertsTabsPath } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/utils';
import { AlertTemplateIdType, YourCustomTemplateId } from '../schema/template';
import { useTemplateStore } from '../store';
import AlertCustomTemplateTab from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/common/AlertCustomTemplateTab';
import { shouldShowAlertV1DevMode } from 'src/utils/app';

export enum AlertTemplateTab {
  Templates = 'templates',
  CustomTemplates = 'customTemplates'
}

export enum AlertTemplatesTabsDisplayName {
  templates = 'Templates',
  customTemplates = 'Custom Template'
}

interface AlertTemplatesNavigationTabsProps {
  tab: AlertTemplateTab;
  onTabChange: (tab: AlertTemplateTab) => void;
}

export const AlertTemplatesNavigationTabs = ({ tab, onTabChange }: AlertTemplatesNavigationTabsProps) => {
  const showCustomTemplates = shouldShowAlertV1DevMode();

  const tabs = [
    {
      label: AlertTemplatesTabsDisplayName.templates,
      onClick: () => onTabChange(AlertTemplateTab.Templates),
      value: AlertTemplateTab.Templates
    }
  ];

  if (showCustomTemplates) {
    tabs.push({
      label: AlertTemplatesTabsDisplayName.customTemplates,
      onClick: () => onTabChange(AlertTemplateTab.CustomTemplates),
      value: AlertTemplateTab.CustomTemplates
    });
  }

  return <GenericNavigationTabs tab={tab} tabs={tabs} />;
};

const AlertTemplatesTopNav = () => {
  return (
    <Flex justifyContent="flex-start" flexDirection="column">
      <Text variant="h2" sx={{ marginBottom: '8px' }}>
        Alert Builder
      </Text>
    </Flex>
  );
};

type AlertTemplatesPageProps = {};

const AlertTemplatesPage: React.FC<AlertTemplatesPageProps> = () => {
  const theme = useStacklineTheme();
  const { beaconNavigator } = useBeaconNavigator();
  const [currentTab, setCurrentTab] = useState<AlertTemplateTab>(AlertTemplateTab.Templates);
  const showCustomTemplates = shouldShowAlertV1DevMode();
  const { alertTemplate } = useTemplateStore();

  const handleCancel = () => {
    beaconNavigator.setPathname(AlertsTabsPath.myAlerts);
    beaconNavigator.go();
  };

  // Reset to Templates tab if Custom Templates is selected but feature flag is disabled
  React.useEffect(() => {
    if (currentTab === AlertTemplateTab.CustomTemplates && !showCustomTemplates) {
      setCurrentTab(AlertTemplateTab.Templates);
    }
  }, [currentTab, showCustomTemplates]);

  const handleCustomTemplateNext = async () => {
    if (Object.keys(alertTemplate).length > 0) {
      // Handle custom template from zustand store
      beaconNavigator.setPathname(AlertsTabsPath.createAlert);
      beaconNavigator.updateSearchParams({ templateId: YourCustomTemplateId as AlertTemplateIdType });
      beaconNavigator.go();
    }
  };

  return (
    <Box>
      <AlertTemplatesTopNav />
      <Box marginBottom={theme.spacing.lg}>
        <AlertTemplatesNavigationTabs tab={currentTab} onTabChange={setCurrentTab} />
        <BasePageContainer>
          {currentTab === AlertTemplateTab.Templates && <AlertTemplatesTab />}
          {currentTab === AlertTemplateTab.CustomTemplates && showCustomTemplates && <AlertCustomTemplateTab />}

          {/* Navigation Buttons */}
          <Flex justifyContent="space-between" alignItems="center" style={{ marginTop: '40px' }}>
            <SlButton variant="normal" onClick={handleCancel}>
              Cancel
            </SlButton>
            {currentTab === AlertTemplateTab.CustomTemplates && (
              <SlButton
                variant="contained"
                onClick={handleCustomTemplateNext}
                disabled={Object.keys(alertTemplate).length === 0}
              >
                Next
              </SlButton>
            )}
          </Flex>
        </BasePageContainer>
      </Box>
    </Box>
  );
};

export default AlertTemplatesPage;
